import { Tables } from "@/integrations/supabase/types";
import { useMemo } from "react";
import { useGradedPricesStore } from "@/stores/useGradedPricesStore";
import { Spinner } from "@/components/ui/spinner";
import NumberFlow from "@number-flow/react";

interface Props {
  cardId: number;
  selectedVariant: Tables<"variants"> | null;
  gradingCompanyId: string | null;
  gradeId: string | null;
  quantity: number;
}

export function AddCardToBinderTotalPrice({
  cardId,
  selectedVariant,
  gradingCompanyId,
  gradeId,
  quantity,
}: Props) {
  const { load: loadGradedPrice, prices: gradedPrices } =
    useGradedPricesStore();

  const isLoadingPrice = useMemo(() => {
    if (!selectedVariant) {
      return false;
    }

    if (!gradeId || !gradingCompanyId) {
      return false;
    }

    const gradedPriceKey = `${selectedVariant.card_id}-${gradingCompanyId}-${gradeId}`;

    return !gradedPrices[gradedPriceKey]?.result;
  }, [gradeId, gradedPrices, gradingCompanyId, selectedVariant]);

  const isUngradedPrice = useMemo(() => {
    if (!selectedVariant) {
      return false;
    }

    if (!gradeId || !gradingCompanyId) {
      return false;
    }

    const gradedPriceKey = `${cardId}-${gradingCompanyId}-${gradeId}`;

    return !gradedPrices[gradedPriceKey]?.result?.data;
  }, [cardId, gradeId, gradedPrices, gradingCompanyId, selectedVariant]);

  const normalizedPrice = useMemo(() => {
    if (!selectedVariant) {
      return null;
    }

    if (!gradeId || !gradingCompanyId) {
      return selectedVariant.price;
    }

    const gradedPriceKey = `${cardId}-${gradingCompanyId}-${gradeId}`;

    const gradedPrice = gradedPrices[gradedPriceKey]?.result?.data;

    return gradedPrice || selectedVariant.price;
  }, [cardId, gradeId, gradedPrices, gradingCompanyId, selectedVariant]);

  const totalPrice = useMemo(() => {
    if (!normalizedPrice) {
      return null;
    }

    return normalizedPrice * quantity;
  }, [normalizedPrice, quantity]);

  return (
    <>
      {isLoadingPrice && <Spinner className="w-6 h-6" />}
      {!isLoadingPrice && (
        <>
          {totalPrice && (
            <div className="text-lg">
              <span className="text-[#16a34a]">
                <NumberFlow
                  value={totalPrice}
                  format={{
                    style: "currency",
                    currency: "USD",
                    // @ts-expect-error - NumberFlow types are not correct
                    trailingZeroDisplay: "stripIfInteger",
                  }}
                />
              </span>
              {isUngradedPrice && (
                <span className="text-muted-foreground"> (ungraded)</span>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
